import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format, differenceInDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { CalendarDays, ArrowRight, ArrowLeft, Download } from 'lucide-react';

const ROOM_DATA = [
  { id: 'classique', name: 'Classique', fullName: 'Habitación Classique', size: '35 m²', price: 290, img: 'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=400&q=80' },
  { id: 'superieure', name: 'Supérieure', fullName: 'Habitación Supérieure', size: '48 m²', price: 390, img: 'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=400&q=80' },
  { id: 'doree', name: 'Suite Dorée', fullName: 'Suite Dorée', size: '68 m²', price: 590, img: 'https://images.unsplash.com/photo-1618773928121-c32242e63f39?w=400&q=80' },
  { id: 'imperiale', name: 'Suite Impériale', fullName: 'Suite Impériale', size: '120 m²', price: 890, img: 'https://images.unsplash.com/photo-1591088398332-8596b4524afe?w=400&q=80' },
];

const EXTRAS = [
  { id: 'transfer', name: 'Transfer aeropuerto', desc: 'Traslado privado de ida o vuelta', price: 85, icon: '🚗' },
  { id: 'champagne', name: 'Champagne de bienvenida', desc: 'Moët & Chandon en habitación', price: 120, icon: '🍾' },
  { id: 'spa', name: 'Circuito Spa para 2', desc: 'Acceso hammam, sauna y piscina', price: 180, icon: '💆' },
  { id: 'dinner', name: 'Cena en Le Jardin', desc: 'Menú degustación para 2 personas', price: 185, icon: '🍽️' },
];

const STEPS = ['Fechas', 'Datos', 'Extras', 'Pago', 'Confirmación'];

function sanitizeInput(str) {
  if (typeof str !== 'string') return '';
  return str.trim().replace(/[<>'"]/g, '').slice(0, 500);
}

function DatePickerField({ label, date, onSelect, minDate, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="booking-field">
      <label>{label}</label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            style={{ width: '100%', padding: '0.85rem 1rem', background: 'var(--onyx)', border: '1px solid rgba(201,169,110,0.15)', color: date ? 'var(--cream)' : 'var(--text-muted)', fontFamily: 'var(--font-ui)', fontSize: 'var(--text-sm)', textAlign: 'left', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            data-testid={testId}
          >
            {date ? format(date, 'dd / MM / yyyy') : 'Seleccionar fecha'}
            <CalendarDays size={16} style={{ opacity: 0.5 }} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start" style={{ background: 'var(--obsidian)', border: '1px solid var(--gold-border)' }}>
          <Calendar
            mode="single"
            selected={date}
            onSelect={(d) => { onSelect(d); setOpen(false); }}
            disabled={(d) => d < (minDate || new Date(new Date().setHours(0,0,0,0)))}
            locale={es}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default function BookingPage() {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [checkin, setCheckin] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [extras, setExtras] = useState([]);
  const [guest, setGuest] = useState({ name: '', email: '', phone: '', country: 'ES', requests: '' });
  const [paying, setPaying] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [cardName, setCardName] = useState('');
  const [confirmationNumber, setConfirmationNumber] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const roomParam = searchParams.get('room');
    if (roomParam) {
      const found = ROOM_DATA.find(r => r.id === roomParam);
      if (found) setSelectedRoom(found.id);
    }
  }, [searchParams]);

  const room = ROOM_DATA.find(r => r.id === selectedRoom);
  const nights = checkin && checkout ? Math.max(0, differenceInDays(checkout, checkin)) : 0;
  const roomTotal = room ? room.price * nights : 0;
  const extrasTotal = extras.reduce((sum, id) => sum + (EXTRAS.find(e => e.id === id)?.price || 0), 0);
  const total = roomTotal + extrasTotal;

  const showError = useCallback((msg) => {
    setError(msg);
    setTimeout(() => setError(''), 4000);
  }, []);

  const goStep = (n) => {
    setStep(n);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const validateStep1 = () => {
    if (!checkin || !checkout) return showError('Selecciona las fechas de entrada y salida');
    if (nights <= 0) return showError('La fecha de salida debe ser posterior a la entrada');
    if (!selectedRoom) return showError('Selecciona una habitación');
    goStep(2);
  };

  const validateStep2 = () => {
    const name = sanitizeInput(guest.name);
    const email = sanitizeInput(guest.email);
    const phone = sanitizeInput(guest.phone);
    if (!name || !email || !phone) return showError('Por favor rellena todos los campos obligatorios');
    if (!email.includes('@')) return showError('Introduce un email válido');
    setGuest({ ...guest, name, email, phone });
    goStep(3);
  };

  const processDemoPayment = () => {
    setPaying(true);
    setTimeout(() => {
      const confNum = 'MD' + Date.now().toString().slice(-6);
      setConfirmationNumber(confNum);
      try {
        localStorage.setItem('lastBooking', JSON.stringify({
          confirmationNumber: confNum, room: selectedRoom, checkin: checkin?.toISOString(), checkout: checkout?.toISOString(),
          nights, adults, children, guest, extras, total, timestamp: new Date().toISOString()
        }));
      } catch(e) { /* localStorage unavailable */ }
      setPaying(false);
      goStep(5);
    }, 2000);
  };

  const downloadPDF = async () => {
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF();
    doc.setFillColor(12, 12, 12);
    doc.rect(0, 0, 210, 35, 'F');
    doc.setTextColor(201, 169, 110);
    doc.setFontSize(20);
    doc.text('Hôtel Maison Dorée', 14, 18);
    doc.setFontSize(10);
    doc.setTextColor(180, 160, 120);
    doc.text('Confirmación de Reserva', 14, 27);
    doc.setTextColor(50, 50, 50);
    doc.setFontSize(12);
    doc.text(`Reserva: ${confirmationNumber}`, 14, 50);
    doc.text(`Huésped: ${guest.name}`, 14, 62);
    doc.text(`Email: ${guest.email}`, 14, 74);
    doc.text(`Habitación: ${room?.fullName}`, 14, 96);
    doc.text(`Check-in: ${checkin ? format(checkin, 'dd/MM/yyyy') : ''}`, 14, 108);
    doc.text(`Check-out: ${checkout ? format(checkout, 'dd/MM/yyyy') : ''}`, 14, 120);
    doc.text(`Noches: ${nights}`, 14, 132);
    doc.setFontSize(13);
    doc.text(`TOTAL: ${total}€`, 14, 152);
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text('Demo: Este es un documento de demostración.', 14, 200);
    doc.save(`reserva-${confirmationNumber}.pdf`);
  };

  const formatCardNum = (v) => setCardNumber(v.replace(/\D/g, '').replace(/(\d{4})/g, '$1 ').trim().slice(0, 19));
  const formatExp = (v) => { let d = v.replace(/\D/g, ''); if (d.length >= 2) d = d.slice(0,2) + ' / ' + d.slice(2); setCardExpiry(d.slice(0, 7)); };

  const toggleExtra = (id) => {
    setExtras(prev => prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]);
  };

  return (
    <div className="booking-page" data-testid="booking-page">
      <div className="booking-page__inner">
        {/* Progress */}
        <div className="booking-progress" data-testid="booking-progress">
          {STEPS.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
              <div className={`booking-progress__step ${i + 1 === step ? 'active' : ''} ${i + 1 < step ? 'done' : ''}`}>
                <span className="booking-progress__num">{i + 1}</span>
                <span className="booking-progress__label">{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className="booking-progress__divider" />}
            </div>
          ))}
        </div>

        {/* Error toast */}
        {error && <div className="booking-toast booking-toast--error" data-testid="booking-error">{error}</div>}

        {/* STEP 1 */}
        {step === 1 && (
          <div data-testid="booking-step-1">
            <h2 className="booking-step__title">¿Cuándo nos visitas?</h2>
            <div className="booking-grid">
              <DatePickerField label="Fecha de entrada" date={checkin} onSelect={setCheckin} testId="booking-checkin" />
              <DatePickerField label="Fecha de salida" date={checkout} onSelect={setCheckout} minDate={checkin ? new Date(checkin.getTime() + 86400000) : undefined} testId="booking-checkout" />
              <div className="booking-field">
                <label>Adultos</label>
                <div className="counter-input">
                  <button type="button" onClick={() => setAdults(Math.max(1, adults - 1))} data-testid="adults-minus">−</button>
                  <input type="number" value={adults} readOnly data-testid="adults-count" />
                  <button type="button" onClick={() => setAdults(Math.min(4, adults + 1))} data-testid="adults-plus">+</button>
                </div>
              </div>
              <div className="booking-field">
                <label>Niños</label>
                <div className="counter-input">
                  <button type="button" onClick={() => setChildren(Math.max(0, children - 1))} data-testid="children-minus">−</button>
                  <input type="number" value={children} readOnly data-testid="children-count" />
                  <button type="button" onClick={() => setChildren(Math.min(3, children + 1))} data-testid="children-plus">+</button>
                </div>
              </div>
            </div>

            <div className="room-selector">
              <h3 className="room-selector__title">Elige tu habitación</h3>
              <div className="room-options">
                {ROOM_DATA.map(r => (
                  <div key={r.id} className={`room-option ${selectedRoom === r.id ? 'selected' : ''}`} onClick={() => setSelectedRoom(r.id)} data-testid={`room-option-${r.id}`}>
                    <img src={r.img} alt={r.name} loading="lazy" />
                    <div className="room-option__info">
                      <span className="room-option__name">{r.name}</span>
                      <span className="room-option__size">{r.size}</span>
                      <span className="room-option__price">{r.price}€/noche</span>
                    </div>
                    <div className="room-option__check">&#10003;</div>
                  </div>
                ))}
              </div>
            </div>

            {nights > 0 && room && (
              <div className="booking-summary" data-testid="booking-summary">
                <div className="booking-summary__row">
                  <span>{room.name} · {nights} noche{nights !== 1 ? 's' : ''}</span>
                  <span>{room.price}€ × {nights} = {roomTotal}€</span>
                </div>
                <div className="booking-summary__row booking-summary__row--total">
                  <span>Total estimado</span>
                  <span className="summary-total">{total}€</span>
                </div>
              </div>
            )}

            <button className="btn-booking-next" onClick={validateStep1} data-testid="step1-next">
              Continuar con mis datos <ArrowRight size={16} style={{ marginLeft: '0.5rem', verticalAlign: 'middle' }} />
            </button>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div data-testid="booking-step-2">
            <h2 className="booking-step__title">Tus datos</h2>
            <div className="booking-grid">
              <div className="booking-field">
                <label>Nombre completo *</label>
                <input type="text" value={guest.name} onChange={e => setGuest({...guest, name: e.target.value})} placeholder="Antoine Dupont" data-testid="guest-name" />
              </div>
              <div className="booking-field">
                <label>Email *</label>
                <input type="email" value={guest.email} onChange={e => setGuest({...guest, email: e.target.value})} placeholder="antoine@email.com" data-testid="guest-email" />
              </div>
              <div className="booking-field">
                <label>Teléfono *</label>
                <input type="tel" value={guest.phone} onChange={e => setGuest({...guest, phone: e.target.value})} placeholder="+34 600 000 000" data-testid="guest-phone" />
              </div>
              <div className="booking-field">
                <label>País de residencia</label>
                <select value={guest.country} onChange={e => setGuest({...guest, country: e.target.value})} data-testid="guest-country">
                  <option value="ES">España</option>
                  <option value="FR">Francia</option>
                  <option value="UK">Reino Unido</option>
                  <option value="DE">Alemania</option>
                  <option value="IT">Italia</option>
                  <option value="other">Otro</option>
                </select>
              </div>
              <div className="booking-field booking-field--full">
                <label>Peticiones especiales</label>
                <textarea value={guest.requests} onChange={e => setGuest({...guest, requests: e.target.value})} placeholder="Habitación alta, almohadas extra, alergias alimentarias..." rows={3} data-testid="guest-requests" />
              </div>
            </div>
            <div className="booking-nav">
              <button className="btn-booking-back" onClick={() => goStep(1)} data-testid="step2-back"><ArrowLeft size={14} /> Atrás</button>
              <button className="btn-booking-next" onClick={validateStep2} data-testid="step2-next">Seleccionar extras <ArrowRight size={14} /></button>
            </div>
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <div data-testid="booking-step-3">
            <h2 className="booking-step__title">Personaliza tu estancia</h2>
            <div className="extras-grid">
              {EXTRAS.map(ex => (
                <div key={ex.id} className={`extra-item ${extras.includes(ex.id) ? 'selected' : ''}`} onClick={() => toggleExtra(ex.id)} data-testid={`extra-${ex.id}`}>
                  <div className="extra-item__icon">{ex.icon}</div>
                  <div className="extra-item__info">
                    <span className="extra-item__name">{ex.name}</span>
                    <span className="extra-item__desc">{ex.desc}</span>
                  </div>
                  <span className="extra-item__price">+{ex.price}€</span>
                  <div className="extra-item__check">&#10003;</div>
                </div>
              ))}
            </div>
            <div className="booking-nav">
              <button className="btn-booking-back" onClick={() => goStep(2)} data-testid="step3-back"><ArrowLeft size={14} /> Atrás</button>
              <button className="btn-booking-next" onClick={() => goStep(4)} data-testid="step3-next">Ver resumen y pagar <ArrowRight size={14} /></button>
            </div>
          </div>
        )}

        {/* STEP 4 */}
        {step === 4 && (
          <div data-testid="booking-step-4">
            <h2 className="booking-step__title">Resumen y pago</h2>
            <div className="checkout-summary" data-testid="checkout-summary">
              <div className="checkout-row"><span>Habitación</span><strong>{room?.fullName || '—'}</strong></div>
              <div className="checkout-row"><span>Check-in</span><strong>{checkin ? format(checkin, 'dd/MM/yyyy') : '—'}</strong></div>
              <div className="checkout-row"><span>Check-out</span><strong>{checkout ? format(checkout, 'dd/MM/yyyy') : '—'}</strong></div>
              <div className="checkout-row"><span>{nights} noches × {room?.price}€</span><strong>{roomTotal}€</strong></div>
              {extrasTotal > 0 && <div className="checkout-row"><span>Extras</span><strong>+{extrasTotal}€</strong></div>}
              <div className="checkout-row checkout-row--total"><span>Total</span><strong>{total}€</strong></div>
            </div>

            <div className="demo-notice" data-testid="demo-notice">
              <span style={{ fontSize: '1.5rem' }}>&#128274;</span>
              <div>
                <strong>Modo demostración</strong>
                <p>Este es un sitio de demostración. Los datos de pago no se procesarán ni se realizará ningún cargo real.</p>
              </div>
            </div>

            <div className="stripe-form" data-testid="stripe-form">
              <div className="stripe-form__header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <svg viewBox="0 0 60 25" width="40" height="16"><rect width="60" height="25" rx="4" fill="#635BFF"/><text x="8" y="18" fill="white" fontFamily="sans-serif" fontSize="14">stripe</text></svg>
                </div>
                <span className="stripe-form__secure">&#128274; Pago seguro</span>
              </div>
              <div className="stripe-form__field">
                <label>Número de tarjeta</label>
                <div className="stripe-input">
                  <input type="text" placeholder="4242 4242 4242 4242" maxLength={19} value={cardNumber} onChange={e => formatCardNum(e.target.value)} data-testid="card-number" />
                  <div className="stripe-input__icons"><span>VISA</span><span>MC</span></div>
                </div>
              </div>
              <div className="stripe-form__row">
                <div className="stripe-form__field">
                  <label>Caducidad</label>
                  <input type="text" placeholder="MM / AA" maxLength={7} value={cardExpiry} onChange={e => formatExp(e.target.value)} data-testid="card-expiry" />
                </div>
                <div className="stripe-form__field">
                  <label>CVC</label>
                  <input type="text" placeholder="123" maxLength={4} value={cardCvc} onChange={e => setCardCvc(e.target.value.replace(/\D/g, '').slice(0, 4))} data-testid="card-cvc" />
                </div>
              </div>
              <div className="stripe-form__field">
                <label>Nombre en la tarjeta</label>
                <input type="text" placeholder="ANTOINE DUPONT" style={{ textTransform: 'uppercase' }} value={cardName} onChange={e => setCardName(e.target.value)} data-testid="card-name" />
              </div>
              <button className="stripe-pay-btn" onClick={processDemoPayment} disabled={paying} data-testid="pay-btn">
                {paying ? 'Procesando...' : `Confirmar reserva · ${total}€`}
              </button>
              <p className="stripe-form__disclaimer">
                Al confirmar aceptas nuestras condiciones de reserva. Cancelación gratuita hasta 48h antes.
              </p>
            </div>

            <div className="booking-nav">
              <button className="btn-booking-back" onClick={() => goStep(3)} data-testid="step4-back"><ArrowLeft size={14} /> Modificar extras</button>
            </div>
          </div>
        )}

        {/* STEP 5 */}
        {step === 5 && (
          <div className="confirmation" data-testid="booking-step-5">
            <div className="confirmation__icon">&#10022;</div>
            <h2 className="confirmation__title">Reserva confirmada</h2>
            <p className="confirmation__subtitle">
              Merci, <span data-testid="conf-guest-name">{guest.name?.split(' ')[0] || 'huésped'}</span>.<br />Esperamos tu llegada con ilusión.
            </p>
            <div className="confirmation__card" data-testid="confirmation-card">
              <div className="conf-row"><span>Nº Reserva</span><strong data-testid="conf-number">{confirmationNumber}</strong></div>
              <div className="conf-row"><span>Habitación</span><strong>{room?.fullName}</strong></div>
              <div className="conf-row"><span>Check-in</span><strong>{checkin ? format(checkin, 'dd/MM/yyyy') : '—'}</strong></div>
              <div className="conf-row"><span>Check-out</span><strong>{checkout ? format(checkout, 'dd/MM/yyyy') : '—'}</strong></div>
              <div className="conf-row"><span>Huésped</span><strong>{guest.name}</strong></div>
              <div className="conf-row conf-row--total"><span>Total</span><strong>{total}€</strong></div>
            </div>
            <div className="confirmation__actions">
              <button onClick={downloadPDF} className="btn-gold" data-testid="download-pdf">
                <Download size={16} /> Descargar PDF
              </button>
              <a href="https://wa.me/34600000000" target="_blank" rel="noopener noreferrer" className="btn-outline-gold">
                WhatsApp
              </a>
            </div>
            <p className="confirmation__email-note">
              <em>Recibirás un email de confirmación en breve. (Demo: no se enviará email real)</em>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
